import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Plus, Trash2, Edit2, Save, Briefcase, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Clock, XCircle, ChevronDown, ChevronUp, FileText
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, Legend, PieChart, Pie
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtCur = (v) => v == null ? '-' : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? '-' : `${v.toFixed(1)}%`;

const STATUS_CONFIG = {
  in_progress: { label: "In Corso", color: "bg-blue-100 text-blue-700 border-blue-300", icon: Clock },
  completed: { label: "Completata", color: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2 },
  cancelled: { label: "Annullata", color: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
};

const ENTRY_TYPES = {
  revenue: { label: "Ricavo", color: "#10b981" },
  material: { label: "Materiale", color: "#f59e0b" },
  labor: { label: "Manodopera", color: "#3b82f6" },
  subcontractor: { label: "Subappalto", color: "#8b5cf6" },
  other_cost: { label: "Altro Costo", color: "#ef4444" },
};

const CHART_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#ec4899", "#14b8a6", "#f97316"];

// ======================== JOB FORM DIALOG ========================
const JobFormDialog = ({ open, onClose, onSave, job }) => {
  const [form, setForm] = useState({ name: '', customer_name: '', status: 'in_progress', start_date: '', end_date: '', estimated_revenue: '', estimated_costs: '', notes: '' });

  useEffect(() => {
    if (job) setForm({
      name: job.name, customer_name: job.customer_name || '', status: job.status || 'in_progress',
      start_date: job.start_date || '', end_date: job.end_date || '',
      estimated_revenue: job.estimated_revenue || '', estimated_costs: job.estimated_costs || '', notes: job.notes || ''
    });
    else setForm({ name: '', customer_name: '', status: 'in_progress', start_date: '', end_date: '', estimated_revenue: '', estimated_costs: '', notes: '' });
  }, [job, open]);

  const estRev = parseFloat(form.estimated_revenue) || 0;
  const estCost = parseFloat(form.estimated_costs) || 0;
  const estMargin = estRev - estCost;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{job ? 'Modifica' : 'Nuova'} Commessa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nome commessa *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Progetto Rossi, Cantiere Milano" data-testid="job-name-input" />
          </div>
          <div>
            <Label>Cliente/Committente</Label>
            <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="es. Rossi Srl" data-testid="job-customer-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">In Corso</SelectItem>
                  <SelectItem value="completed">Completata</SelectItem>
                  <SelectItem value="cancelled">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Inizio</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="job-start-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ricavo Preventivato</Label>
              <Input type="number" min="0" value={form.estimated_revenue} onChange={e => setForm(f => ({ ...f, estimated_revenue: e.target.value }))} placeholder="0" data-testid="job-est-revenue" />
            </div>
            <div>
              <Label>Costo Preventivato</Label>
              <Input type="number" min="0" value={form.estimated_costs} onChange={e => setForm(f => ({ ...f, estimated_costs: e.target.value }))} placeholder="0" data-testid="job-est-costs" />
            </div>
          </div>
          {estRev > 0 && (
            <div className="bg-slate-50 rounded-lg p-2 text-sm flex justify-between">
              <span className="text-muted-foreground">Margine preventivato:</span>
              <span className={`font-bold ${estMargin >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmtCur(estMargin)} ({fmtPct(estRev > 0 ? estMargin / estRev * 100 : 0)})</span>
            </div>
          )}
          <div>
            <Label>Note</Label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Note opzionali" data-testid="job-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave({ ...form, estimated_revenue: parseFloat(form.estimated_revenue) || 0, estimated_costs: parseFloat(form.estimated_costs) || 0 })} disabled={!form.name.trim()} data-testid="save-job-btn">
            <Save className="h-4 w-4 mr-1" />Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== ENTRY FORM DIALOG ========================
const EntryFormDialog = ({ open, onClose, onSave }) => {
  const [form, setForm] = useState({ entry_type: 'revenue', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });

  useEffect(() => {
    if (open) setForm({ entry_type: 'revenue', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Aggiungi Voce</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tipo</Label>
            <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
              <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ENTRY_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descrizione *</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="es. Fattura fornitura materiali" data-testid="entry-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Importo (EUR)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" data-testid="entry-amount-input" />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="entry-date-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave({ ...form, amount: parseFloat(form.amount) || 0 })} disabled={!form.description.trim()} data-testid="save-entry-btn">
            <Plus className="h-4 w-4 mr-1" />Aggiungi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== JOB DETAIL PANEL ========================
const JobDetailPanel = ({ job, clientId, onUpdate, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/jobs/${job.id}/entries`, { headers });
      setEntries(res.data.entries);
    } catch { toast.error("Errore caricamento voci"); }
    finally { setLoading(false); }
  }, [clientId, job.id]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const addEntry = async (data) => {
    try {
      await axios.post(`${API}/clients/${clientId}/jobs/${job.id}/entries`, data, { headers });
      toast.success("Voce aggiunta");
      setShowEntryForm(false);
      fetchEntries();
      onUpdate();
    } catch { toast.error("Errore aggiunta voce"); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Eliminare questa voce?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/jobs/${job.id}/entries/${entryId}`, { headers });
      toast.success("Voce eliminata");
      fetchEntries();
      onUpdate();
    } catch { toast.error("Errore eliminazione"); }
  };

  const revenue = entries.filter(e => e.entry_type === 'revenue').reduce((s, e) => s + e.amount, 0);
  const costs = entries.filter(e => e.entry_type !== 'revenue').reduce((s, e) => s + e.amount, 0);
  const margin = revenue - costs;

  // Cost breakdown for pie chart
  const costsByType = {};
  entries.filter(e => e.entry_type !== 'revenue').forEach(e => {
    costsByType[e.entry_type] = (costsByType[e.entry_type] || 0) + e.amount;
  });
  const pieData = Object.entries(costsByType).map(([k, v]) => ({
    name: ENTRY_TYPES[k]?.label || k, value: v, fill: ENTRY_TYPES[k]?.color || "#999"
  }));

  const sc = STATUS_CONFIG[job.status] || STATUS_CONFIG.in_progress;

  return (
    <div className="space-y-4" data-testid="job-detail-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-indigo-600" />
            {job.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {job.customer_name && <span className="text-sm text-muted-foreground">{job.customer_name}</span>}
            <Badge className={`${sc.color} text-xs`}>{sc.label}</Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowEntryForm(true)} data-testid="add-entry-btn">
            <Plus className="h-4 w-4 mr-1" />Voce
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="close-detail-btn">Chiudi</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border p-3 bg-emerald-50/50">
          <div className="text-xs text-muted-foreground">Ricavi</div>
          <div className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="job-detail-revenue">{fmtCur(revenue)}</div>
        </div>
        <div className="rounded-lg border p-3 bg-red-50/50">
          <div className="text-xs text-muted-foreground">Costi</div>
          <div className="text-lg font-bold text-red-600 tabular-nums" data-testid="job-detail-costs">{fmtCur(costs)}</div>
        </div>
        <div className={`rounded-lg border p-3 ${margin >= 0 ? 'bg-blue-50/50' : 'bg-red-50/50'}`}>
          <div className="text-xs text-muted-foreground">Margine</div>
          <div className={`text-lg font-bold tabular-nums ${margin >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="job-detail-margin">
            {fmtCur(margin)} <span className="text-xs font-normal">({fmtPct(revenue > 0 ? margin / revenue * 100 : 0)})</span>
          </div>
        </div>
      </div>

      {/* Variance (if estimates exist) */}
      {(job.estimated_revenue > 0 || job.estimated_costs > 0) && (
        <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
          <div className="font-medium text-xs text-muted-foreground uppercase">Scostamento dal preventivo</div>
          <div className="flex justify-between">
            <span>Ricavi: {fmtCur(job.estimated_revenue)} prev.</span>
            <span className={revenue >= job.estimated_revenue ? 'text-green-600' : 'text-red-600'}>
              {revenue >= job.estimated_revenue ? '+' : ''}{fmtCur(revenue - job.estimated_revenue)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Costi: {fmtCur(job.estimated_costs)} prev.</span>
            <span className={costs <= job.estimated_costs ? 'text-green-600' : 'text-red-600'}>
              {costs > job.estimated_costs ? '+' : ''}{fmtCur(costs - job.estimated_costs)}
            </span>
          </div>
        </div>
      )}

      {/* Cost Breakdown Pie */}
      {pieData.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ripartizione Costi</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtCur(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Entries Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Voci Registrate ({entries.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">Caricamento...</div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Nessuna voce registrata. Aggiungi ricavi e costi.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id} data-testid={`job-entry-${e.id}`}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs" style={{ borderColor: ENTRY_TYPES[e.entry_type]?.color, color: ENTRY_TYPES[e.entry_type]?.color }}>
                        {ENTRY_TYPES[e.entry_type]?.label || e.entry_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{e.description}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${e.entry_type === 'revenue' ? 'text-green-700' : 'text-red-600'}`}>
                      {e.entry_type === 'revenue' ? '+' : '-'}{fmtCur(e.amount)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.date}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteEntry(e.id)} data-testid={`delete-job-entry-${e.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EntryFormDialog open={showEntryForm} onClose={() => setShowEntryForm(false)} onSave={addEntry} />
    </div>
  );
};

// ======================== MAIN PAGE ========================
export const JobProfitabilityPage = ({ clientId }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewTab, setViewTab] = useState("list");
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/jobs/analysis`, { headers });
      setAnalysis(res.data);
    } catch { toast.error("Errore caricamento commesse"); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const saveJob = async (data) => {
    try {
      if (editingJob) {
        await axios.put(`${API}/clients/${clientId}/jobs/${editingJob.id}`, data, { headers });
        toast.success("Commessa aggiornata");
      } else {
        await axios.post(`${API}/clients/${clientId}/jobs`, data, { headers });
        toast.success("Commessa creata");
      }
      setShowJobForm(false);
      setEditingJob(null);
      fetchAnalysis();
    } catch { toast.error("Errore salvataggio"); }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Eliminare questa commessa e tutte le sue voci?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/jobs/${jobId}`, { headers });
      toast.success("Commessa eliminata");
      if (selectedJob?.id === jobId) setSelectedJob(null);
      fetchAnalysis();
    } catch { toast.error("Errore eliminazione"); }
  };

  if (loading) return <div className="flex justify-center py-12 text-muted-foreground">Caricamento commesse...</div>;

  const jobs = analysis?.jobs || [];
  const summary = analysis?.summary || {};

  // Chart data
  const marginBarData = jobs.filter(j => j.actual_revenue > 0 || j.actual_costs > 0).slice(0, 15).map(j => ({
    name: j.name.length > 18 ? j.name.slice(0, 18) + '...' : j.name,
    Ricavi: j.actual_revenue,
    Costi: j.actual_costs,
    Margine: j.actual_margin,
  }));

  const marginRankData = jobs.filter(j => j.actual_revenue > 0).map(j => ({
    name: j.name.length > 20 ? j.name.slice(0, 20) + '...' : j.name,
    value: j.margin_pct,
    fill: j.margin_pct >= 20 ? '#10b981' : j.margin_pct >= 0 ? '#f59e0b' : '#ef4444',
  }));

  return (
    <div className="space-y-6" data-testid="job-profitability-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-indigo-600" />
            Margine per Commessa
          </h2>
          <p className="text-sm text-muted-foreground">
            Analisi della redditivita per singola commessa o progetto
          </p>
        </div>
        <Button onClick={() => { setEditingJob(null); setShowJobForm(true); }} data-testid="new-job-btn">
          <Plus className="h-4 w-4 mr-1" />Nuova Commessa
        </Button>
      </div>

      {/* Summary Cards */}
      {jobs.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Commesse</div>
              <div className="text-xl font-bold" data-testid="total-jobs">{summary.total_jobs}</div>
              <div className="flex gap-1 mt-1">
                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">{summary.status_counts?.in_progress || 0} attive</Badge>
                <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700">{summary.status_counts?.completed || 0} chiuse</Badge>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Ricavi Totali</div>
              <div className="text-xl font-bold tabular-nums text-emerald-700" data-testid="total-revenue">{fmtCur(summary.total_revenue)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Costi Totali</div>
              <div className="text-xl font-bold tabular-nums text-red-600" data-testid="total-costs">{fmtCur(summary.total_costs)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Margine Totale</div>
              <div className={`text-xl font-bold tabular-nums ${summary.total_margin >= 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="total-margin">{fmtCur(summary.total_margin)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-3 pb-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Margine Medio</div>
              <div className={`text-xl font-bold tabular-nums ${summary.avg_margin_pct >= 0 ? 'text-amber-700' : 'text-red-700'}`} data-testid="avg-margin">{fmtPct(summary.avg_margin_pct)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Selected Job Detail */}
      {selectedJob && (
        <Card className="border-indigo-200 bg-indigo-50/20">
          <CardContent className="pt-4">
            <JobDetailPanel job={selectedJob} clientId={clientId} onUpdate={fetchAnalysis} onClose={() => setSelectedJob(null)} />
          </CardContent>
        </Card>
      )}

      {/* Tabs: List & Charts */}
      <Tabs value={viewTab} onValueChange={setViewTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">Elenco Commesse</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts">Grafici</TabsTrigger>
        </TabsList>

        {/* LIST TAB */}
        <TabsContent value="list">
          {jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nessuna commessa</h3>
                <p className="text-muted-foreground mb-4">Crea la prima commessa per iniziare l'analisi per progetto</p>
                <Button onClick={() => { setEditingJob(null); setShowJobForm(true); }} data-testid="empty-new-job-btn">
                  <Plus className="h-4 w-4 mr-1" />Nuova Commessa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Ricavi</TableHead>
                      <TableHead className="text-right">Costi</TableHead>
                      <TableHead className="text-right">Margine</TableHead>
                      <TableHead className="text-right">%</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((j) => {
                      const sc = STATUS_CONFIG[j.status] || STATUS_CONFIG.in_progress;
                      const hasVariance = j.estimated_revenue > 0;
                      const revVar = hasVariance ? j.actual_revenue - j.estimated_revenue : null;
                      return (
                        <TableRow key={j.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedJob(j)} data-testid={`job-row-${j.id}`}>
                          <TableCell>
                            <div className="font-medium">{j.name}</div>
                            {j.start_date && <div className="text-xs text-muted-foreground">Dal {j.start_date}</div>}
                          </TableCell>
                          <TableCell className="text-sm">{j.customer_name || '-'}</TableCell>
                          <TableCell>
                            <Badge className={`${sc.color} text-xs`} variant="outline">{sc.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtCur(j.actual_revenue)}
                            {revVar != null && (
                              <div className={`text-[10px] ${revVar >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {revVar >= 0 ? '+' : ''}{fmtCur(revVar)} vs prev.
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-red-600">{fmtCur(j.actual_costs)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-bold ${j.actual_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {fmtCur(j.actual_margin)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <span className={j.margin_pct >= 20 ? 'text-green-600' : j.margin_pct >= 0 ? 'text-amber-600' : 'text-red-600'}>
                              {fmtPct(j.margin_pct)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingJob(j); setShowJobForm(true); }} data-testid={`edit-job-${j.id}`}>
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteJob(j.id)} data-testid={`delete-job-${j.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CHARTS TAB */}
        <TabsContent value="charts" className="space-y-4">
          {jobs.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Nessun dato da visualizzare</CardContent></Card>
          ) : (
            <>
              {/* Revenue vs Costs Bar */}
              {marginBarData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Ricavi vs Costi per Commessa</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={marginBarData} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal />
                        <XAxis type="number" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                        <Tooltip formatter={(v) => fmtCur(v)} />
                        <Legend />
                        <Bar dataKey="Ricavi" fill="#10b981" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="Costi" fill="#ef4444" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Margin % Ranking */}
              {marginRankData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-base">Classifica Margine %</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(200, marginRankData.length * 36)}>
                      <BarChart data={marginRankData} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal />
                        <XAxis type="number" tickFormatter={v => `${v}%`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
                        <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                        <Bar dataKey="value" name="Margine %" radius={[0, 4, 4, 0]}>
                          {marginRankData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <JobFormDialog open={showJobForm} onClose={() => { setShowJobForm(false); setEditingJob(null); }} onSave={saveJob} job={editingJob} />
    </div>
  );
};
